import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputTextModule } from 'primeng/inputtext';
import { filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthTwinTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { StructuredJsonFormComponent } from '../../shared/design-system/structured-json-form.component';
import { combineUtcDateAndTime, defaultCalendarFromUtc, defaultCalendarToUtc, splitUtcDateTime } from './growth-datetime.util';
import {
  emptyTwinWhatIfPayloadJson,
  growthTwinAdvisoryDisplayFields,
  growthTwinSimulationDisplayFields,
  growthTwinWhatIfFields
} from './growth-service-form.models';
import { GrowthScenario, GrowthTwinService } from './growth-twin.service';

type ScenarioTab = 'whatIf' | 'simulation' | 'advisory';

@Component({
  standalone: true,
  selector: 'ain-growth-twin-scenario-detail',
  providers: [ConfirmationService],
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    ConfirmDialogModule,
    JalaliDateInputComponent,
    StructuredJsonFormComponent,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page
      [title]="isCreateMode() ? t('growth.twin.scenarioDetail.form.createTitle') : t('growth.twin.scenarioDetail.form.editTitle')"
      [description]="t('growth.twin.scenarioDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.twin.scenarioDetail.help.title'"
        [descriptionKey]="'growth.twin.scenarioDetail.help.description'"
        [bulletKeys]="['growth.twin.scenarioDetail.help.bullets.draft', 'growth.twin.scenarioDetail.help.bullets.simulate']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/growth/twin/scenarios']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            @if (!isCreateMode() && canSimulate()) {
              <button
                pButton
                type="button"
                [label]="t('growth.twin.scenarioDetail.actions.simulate')"
                severity="secondary"
                [disabled]="simulating()"
                (click)="confirmSimulate()"></button>
            }
            <button
              pButton
              type="button"
              [label]="t('common.save')"
              [disabled]="saving() || (!isCreateMode() && !isDraft())"
              (click)="save()"></button>
          </div>
        </div>

        <section class="card">
          <h3>{{ t('growth.twin.scenarioDetail.sections.details') }}</h3>
          <div class="grid">
            <label>
              <span>{{ t('growth.twin.scenarioDetail.fields.name') }}</span>
              <input pInputText type="text" [(ngModel)]="form.name" [disabled]="!isCreateMode() && !isDraft()" />
            </label>
            <label>
              <span>{{ t('growth.twin.scenarioDetail.fields.status') }}</span>
              <input pInputText type="text" [ngModel]="scenario()?.status ?? (isCreateMode() ? 'Draft' : '-')" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.twin.scenarioDetail.fields.boundary') }}</span>
              <input pInputText type="text" [ngModel]="scenario()?.boundary ?? 'growth-shadow'" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.twin.scenarios.form.periodStart') }}</span>
              <ain-date-input [(ngModel)]="form.periodStartUtc" [disabled]="!isCreateMode() && !isDraft()" />
            </label>
            <label>
              <span>{{ t('growth.twin.scenarios.form.periodEnd') }}</span>
              <ain-date-input [(ngModel)]="form.periodEndUtc" [disabled]="!isCreateMode() && !isDraft()" />
            </label>
            <label class="wide">
              <span>{{ t('growth.twin.scenarioDetail.fields.simulationRef') }}</span>
              <input pInputText type="text" [ngModel]="scenario()?.simulationRef ?? '-'" [disabled]="true" />
            </label>
            <label class="wide">
              <span>{{ t('growth.twin.scenarioDetail.fields.disclaimer') }}</span>
              <input pInputText type="text" [ngModel]="scenario()?.disclaimer ?? t('growth.twin.businessTwin.disclaimer.shadow')" [disabled]="true" />
            </label>
          </div>
        </section>

        <section class="card">
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'whatIf'" (click)="activeTab.set('whatIf')">
              {{ t('growth.twin.scenarioDetail.tabs.whatIf') }}
            </button>
            <button type="button" [class.active]="activeTab() === 'simulation'" (click)="activeTab.set('simulation')">
              {{ t('growth.twin.scenarioDetail.tabs.simulation') }}
            </button>
            <button type="button" [class.active]="activeTab() === 'advisory'" (click)="activeTab.set('advisory')">
              {{ t('growth.twin.scenarioDetail.tabs.advisory') }}
            </button>
          </div>

          @if (activeTab() === 'whatIf') {
            <ain-info-hint
              [titleKey]="'growth.twin.scenarioDetail.help.whatIf.title'"
              [descriptionKey]="'growth.twin.scenarioDetail.help.whatIf.description'"
              [bulletKeys]="['growth.twin.scenarioDetail.help.whatIf.bullets.percentages']" />
            <ain-structured-json-form
              [fields]="whatIfFields"
              [(payloadJson)]="whatIfPayloadJson"
              [readonly]="!isCreateMode() && !isDraft()" />
          }

          @if (activeTab() === 'simulation') {
            <ain-info-hint
              [titleKey]="'growth.twin.scenarioDetail.help.simulation.title'"
              [descriptionKey]="'growth.twin.scenarioDetail.help.simulation.description'"
              [bulletKeys]="['growth.twin.scenarioDetail.help.simulation.bullets.shadow']" />
            <ain-structured-json-display
              [fields]="simulationFields"
              [payloadJson]="scenario()?.simulateResultJson ?? '{}'"
              [emptyMessage]="t('growth.twin.scenarioDetail.simulation.empty')"
              labelKeyPrefix="growth.twin.scenarioDetail.simulation.fields" />
          }

          @if (activeTab() === 'advisory') {
            <ain-info-hint
              [titleKey]="'growth.twin.scenarioDetail.help.advisory.title'"
              [descriptionKey]="'growth.twin.scenarioDetail.help.advisory.description'"
              [bulletKeys]="['growth.twin.scenarioDetail.help.advisory.bullets.optimizer']" />
            <ain-structured-json-display
              [fields]="advisoryFields"
              [payloadJson]="scenario()?.advisoryRecommendationJson ?? '{}'"
              [emptyMessage]="t('growth.twin.scenarioDetail.advisory.empty')"
              labelKeyPrefix="growth.twin.scenarioDetail.advisory.fields" />
          }
        </section>

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
      <p-confirmDialog />
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3 { margin: 0; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    .wide { grid-column: span 3; }
    label { display: grid; gap: 6px; }
    .tabs { display: flex; flex-wrap: wrap; gap: 8px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
    .tabs button { border: 0; background: transparent; padding: 6px 10px; border-radius: 8px; cursor: pointer; color: #334155; }
    .tabs button.active { background: #dbeafe; color: #1e40af; font-weight: 600; }
    .message.error { color: #b91c1c; }
    @media (max-width: 1200px) {
      .grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .wide { grid-column: span 2; }
    }
    @media (max-width: 760px) {
      .grid { grid-template-columns: 1fr; }
      .wide { grid-column: span 1; }
    }
  `]
})
export class GrowthTwinScenarioDetailComponent implements OnInit {
  private readonly api = inject(GrowthTwinService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly confirmation = inject(ConfirmationService);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly simulating = signal(false);
  readonly errorMessage = signal('');
  readonly isCreateMode = signal(false);
  readonly scenario = signal<GrowthScenario | null>(null);
  readonly activeTab = signal<ScenarioTab>('whatIf');

  readonly whatIfFields = growthTwinWhatIfFields;
  readonly simulationFields = growthTwinSimulationDisplayFields;
  readonly advisoryFields = growthTwinAdvisoryDisplayFields;

  whatIfPayloadJson = emptyTwinWhatIfPayloadJson();

  private scenarioId = '';
  private subscribedTopic: string | null = null;

  form = {
    name: '',
    periodStartUtc: defaultCalendarFromUtc() as string | null,
    periodEndUtc: defaultCalendarToUtc() as string | null
  };

  ngOnInit(): void {
    this.scenarioId = this.route.snapshot.paramMap.get('scenarioId') ?? '';
    this.isCreateMode.set(!this.scenarioId || this.scenarioId === 'new');

    this.liveUpdateService.events$
      .pipe(
        filter(event => {
          if (event.eventType !== LiveUpdateEventTypes.growthTwinChanged) {
            return false;
          }
          if (this.isCreateMode()) {
            return false;
          }
          const payloadScenarioId = event.payload['scenarioId'];
          return payloadScenarioId === undefined || String(payloadScenarioId).toLowerCase() === this.scenarioId.toLowerCase();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();

    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }
    this.load(true);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  isDraft(): boolean {
    return this.isCreateMode() || this.scenario()?.status === 'Draft';
  }

  canSimulate(): boolean {
    return !!this.scenario();
  }

  confirmSimulate(): void {
    this.confirmation.confirm({
      header: this.t('growth.twin.scenarioDetail.confirm.simulate.title'),
      message: this.t('growth.twin.scenarioDetail.confirm.simulate.message'),
      acceptLabel: this.t('growth.twin.scenarioDetail.confirm.simulate.accept'),
      rejectLabel: this.t('common.cancel'),
      accept: () => this.simulate()
    });
  }

  load(showLoading = true): void {
    if (!this.scenarioId || this.scenarioId === 'new') {
      this.loading.set(false);
      return;
    }
    if (showLoading) {
      this.loading.set(true);
    }
    this.errorMessage.set('');
    this.api.getScenario(this.scenarioId).subscribe({
      next: row => {
        this.scenario.set(row);
        this.populateForm(row);
        this.loading.set(false);
      },
      error: () => {
        this.scenario.set(null);
        this.loading.set(false);
        this.errorMessage.set(this.t('growth.twin.scenarioDetail.messages.loadFailed'));
      }
    });
  }

  save(): void {
    if (!this.form.name.trim()) {
      this.errorMessage.set(this.t('growth.twin.scenarioDetail.messages.nameRequired'));
      return;
    }

    this.saving.set(true);
    this.errorMessage.set('');
    const payload = {
      name: this.form.name.trim(),
      whatIfJson: this.whatIfPayloadJson,
      periodStartUtc: combineUtcDateAndTime(this.form.periodStartUtc, '00:00'),
      periodEndUtc: combineUtcDateAndTime(this.form.periodEndUtc, '23:59'),
      campaignId: this.scenario()?.campaignId ?? null
    };

    if (this.isCreateMode()) {
      this.api.createScenario(payload).subscribe({
        next: created => {
          this.saving.set(false);
          void this.router.navigate(['/growth/twin/scenarios', created.id]);
        },
        error: () => {
          this.saving.set(false);
          this.errorMessage.set(this.t('growth.twin.scenarioDetail.messages.saveFailed'));
        }
      });
      return;
    }

    this.api.updateScenario(this.scenarioId, payload).subscribe({
      next: row => {
        this.scenario.set(row);
        this.populateForm(row);
        this.saving.set(false);
      },
      error: () => {
        this.saving.set(false);
        this.errorMessage.set(this.t('growth.twin.scenarioDetail.messages.saveFailed'));
      }
    });
  }

  simulate(): void {
    if (!this.scenarioId || this.scenarioId === 'new') {
      return;
    }
    this.simulating.set(true);
    this.errorMessage.set('');
    this.api.simulateScenario(this.scenarioId).subscribe({
      next: row => {
        this.scenario.set(row);
        this.populateForm(row);
        this.activeTab.set('simulation');
        this.simulating.set(false);
      },
      error: () => {
        this.simulating.set(false);
        this.errorMessage.set(this.t('growth.twin.scenarioDetail.messages.simulateFailed'));
      }
    });
  }

  private populateForm(row: GrowthScenario): void {
    this.form = {
      name: row.name,
      periodStartUtc: splitUtcDateTime(row.periodStartUtc).dateIso,
      periodEndUtc: splitUtcDateTime(row.periodEndUtc).dateIso
    };
    this.whatIfPayloadJson = row.whatIfJson?.trim() || emptyTwinWhatIfPayloadJson();
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthTwinTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
